import { useMutation } from 'react-query';
import { v4 as uuidv4 } from 'uuid';

import { azureUpload, getAzureSignedLinkOutlet } from '../api';
import { getFileNameWithFixedJpe } from '../../../common/utils';
import { queueFileUpload } from '../../../common/offline/fileStorage';
import { addLocalPhoto } from '../../../common/offline/offlinePhotoStorage';
import { isCurrentlyOnline, useOnlineStatus } from '../../../common/offline/useOnlineStatus';

type UploadResult = {
  url: string;
  isOfflineQueued: boolean;
  uploadId?: string;
};

type UploadParams = {
  file: File;
  outletId: string;
  propertyId: number;
  uploadType?: 'outlet' | 'tpOutlet';
};

export const useAzureOutletPhotoUpload = () => {
  const { isOnline } = useOnlineStatus();

  const { data, mutate, isLoading, isError, isSuccess } = useMutation<UploadResult, unknown, UploadParams>(
    async ({ file, outletId, propertyId, uploadType = 'outlet' }) => {
      // Check online status at mutation time
      if (!isCurrentlyOnline()) {
        // Offline - queue the file for later upload
        // The fileSyncManager will upload the file AND send the mutation with the real URL
        const pendingUpload = await queueFileUpload(
          file,
          uploadType, // Entity type - used by fileSyncManager to know mutation type
          outletId, // Entity ID - the outlet this photo belongs to
          propertyId, // Property ID - needed for the mutation
          uploadType, // Upload type - 'outlet' or 'tpOutlet'
        );

        // Track the local URL in offlinePhotoStorage for display and cleanup
        if (pendingUpload.localUrl) {
          addLocalPhoto(uploadType, outletId, pendingUpload.localUrl, pendingUpload.id);
        }

        // Return the local URL for immediate display
        // The mutation will NOT be called - fileSyncManager handles it after file upload
        return {
          url: pendingUpload.localUrl!,
          isOfflineQueued: true,
          uploadId: pendingUpload.id,
        };
      }

      // Online - upload directly to Azure
      const { url, query } = await getAzureSignedLinkOutlet();

      const fileName = `${uuidv4()}-${getFileNameWithFixedJpe(file!.name)}`;

      await azureUpload(new File([file!], fileName!), `${url}?${query}`);

      return {
        url: `${url}outlet/${fileName}`,
        isOfflineQueued: false,
      };
    },
  );

  return {
    data: data?.url,
    mutate,
    isLoading,
    isError,
    isSuccess,
    isOfflineQueued: data?.isOfflineQueued ?? false,
    uploadId: data?.uploadId,
    isOnline,
  };
};
